// @givmo/board — the board's committees (CBG-YR6-REG, frames c1 and c1b; CBG-YR6-AUTH's column).
//
// One row per standing or ad hoc committee that stands, Ramp's People table as the reference: plain
// words, an em dash for an empty cell, no badge. The "Acts for the board" column is the plan §4
// "classification view": the organization's own answer to line 8b's question for each committee,
// never Board's reading — "—" until an Owner records one, and nothing nags about it. It is drawn
// only where the organization's type is asked line 8b (`authority_question_offered`, class data). A
// dissolved committee leaves one quiet line under the table. With no committee at all (GCF's case),
// the empty state quotes the IRS's own sentence about line 8b, with where it is written — again only
// where the question is asked — and says nothing else.
//
// "Next meeting" (CBG-YR6-MEET, frame c1): the committee's soonest meeting that stands, by its day in
// the meeting's own zone, from the list read itself; "—" with none.

import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { BoardCommitteeSummaryOut } from '@givmo/api-client';
import { Button, DataTable, EmptyState, Icon } from '@givmo/design-system';
import { Network } from 'lucide-react';
import { useBoardHost, useBoardPermission } from '../../board-host';
import { useBoardOperations } from '../../board-runtime';
import { Link, useNavigate } from '../../board-routing';
import { HowThisWorks } from '../../components/fine-print';
import { PageShell } from '../../components/page-shell';
import { meetingDay } from '../meetings/meetings-decode';
import { AddCommitteeDialog } from './add-committee-dialog';
import { committeeMeetingsWords as cm } from './committee-meetings-words';
import { authorityQuestionOffered, dissolvedNames, extractCommittees } from './committees-decode';
import { committeesWords as w } from './committees-words';

const NAME_LINK =
  'font-sans text-s15 font-w600 tracking-givmo text-text-strong outline-none transition-colors hover:text-brand-700 focus-visible:underline';
const QUIET = 'font-sans text-s12 font-w400 tracking-givmo text-text-tertiary';

export function CommitteesPage() {
  const { orgRef, isReady } = useBoardHost();
  const operations = useBoardOperations();
  const navigate = useNavigate();
  const canManage = useBoardPermission('board_member:manage');
  const [adding, setAdding] = useState(false);

  const listQuery = useQuery({
    ...operations.officerListBoardCommitteesOptions({ path: { org_ref: orgRef ?? '' } }),
    enabled: isReady && orgRef !== null,
  });
  const all = extractCommittees(listQuery.data);
  const offered = authorityQuestionOffered(listQuery.data);
  const active = all.filter((committee) => committee.status === 'active');
  const dissolved = all.filter((committee) => committee.status === 'dissolved');

  const columns = useMemo(
    () => [
      {
        id: 'name',
        header: w.columnCommittee,
        stacked: 'heading' as const,
        cell: (committee: BoardCommitteeSummaryOut) => (
          <span className="flex min-w-0 flex-col gap-2">
            <Link
              to="/committees/$committeeId"
              params={{ committeeId: committee.id }}
              className={NAME_LINK}
              data-testid={`committees-link-${committee.id}`}
            >
              {committee.name}
            </Link>
            {committee.continuity === 'ad_hoc' ? (
              <span className={QUIET}>{w.continuity(committee.continuity)}</span>
            ) : null}
          </span>
        ),
      },
      {
        id: 'chair',
        header: w.columnChair,
        stackedLabel: w.columnChair,
        cell: (committee: BoardCommitteeSummaryOut) => committee.chair?.display_name ?? '—',
        width: '220px',
      },
      {
        id: 'next',
        header: cm.columnNextMeeting,
        stackedLabel: cm.columnNextMeeting,
        cell: (committee: BoardCommitteeSummaryOut) => (
          <span className="tabular-nums" data-testid={`committees-next-${committee.id}`}>
            {committee.next_meeting ? meetingDay(committee.next_meeting) : '—'}
          </span>
        ),
        width: '160px',
      },
      ...(offered
        ? [
            {
              id: 'acts',
              header: w.columnActsForBoard,
              stackedLabel: w.columnActsForBoard,
              cell: (committee: BoardCommitteeSummaryOut) => (
                <span data-testid={`committees-acts-${committee.id}`}>
                  {w.answerShort(committee.authority_answer)}
                </span>
              ),
              width: '170px',
            },
          ]
        : []),
    ],
    [offered],
  );

  return (
    <PageShell
      title={w.pageTitle}
      description={
        offered ? (
          <>
            {cm.pageDescription} <HowThisWorks testId="committees-how" lines={w.actsForBoardHow} />
          </>
        ) : (
          cm.pageDescription
        )
      }
      actions={
        canManage ? (
          <Button
            appearance="saas"
            tone="primary"
            disabled={!isReady || orgRef === null}
            onClick={() => setAdding(true)}
            testId="committees-add"
          >
            {w.addCommittee}
          </Button>
        ) : undefined
      }
      testId="page-committees"
    >
      <DataTable<BoardCommitteeSummaryOut>
        testId="committees-table"
        stackAt="sm"
        columns={columns}
        rows={active}
        rowKey={(committee) => committee.id}
        rowTestId={(committee) => `committees-row-${committee.id}`}
        density="comfortable"
        loading={listQuery.isPending && isReady}
        loadingTestId="committees-loading"
        error={listQuery.isError ? w.loadFailed : undefined}
        errorTestId="committees-error"
        retryTestId="committees-error-retry"
        onRetry={() => void listQuery.refetch()}
        emptyState={
          <EmptyState
            testId="committees-empty"
            icon={<Icon icon={Network} size={20} aria-hidden />}
            title={w.emptyTitle}
            description={
              <span className="flex flex-col gap-10">
                {canManage ? <span>{w.emptyLine}</span> : null}
                {offered && dissolved.length === 0 ? (
                  <span
                    className="flex flex-col gap-2 text-text-tertiary"
                    data-testid="committees-empty-instruction"
                  >
                    <span>{w.emptyInstructionQuote}</span>
                    <span>{w.emptyInstructionSource}</span>
                  </span>
                ) : null}
              </span>
            }
          />
        }
      />

      {dissolved.length > 0 ? (
        <p
          className="font-sans text-s13 font-w400 leading-h15 tracking-givmo text-text-tertiary"
          data-testid="committees-dissolved"
        >
          {w.dissolvedLine(dissolvedNames(dissolved))}
        </p>
      ) : null}

      {adding && orgRef !== null ? (
        <AddCommitteeDialog
          orgRef={orgRef}
          onClose={() => setAdding(false)}
          onAdded={(committee, formId) => {
            setAdding(false);
            void listQuery.refetch();
            void navigate({
              to: '/committees/$committeeId',
              params: { committeeId: committee.id },
              ...(formId !== null ? { search: { charter: formId } } : {}),
            });
          }}
        />
      ) : null}
    </PageShell>
  );
}
